import { Component, Input, OnDestroy, OnInit, Injector } from '@angular/core';
import { FormTemplateService } from '../../services/form-template.service';
import { defer, Observable, Subscription, BehaviorSubject, of, from, forkJoin } from 'rxjs';
import FormTemplate from '../../entities/form-template.entity';
import { finalize } from 'rxjs/operators/finalize';
import { take } from 'rxjs/operators/take';
import { MatDialog } from '@angular/material';
import { PreviewComponent, PreviewPdfData } from './components/preview/preview.component';
import { CustomFieldsComponent } from './components/custom-fields/custom-fields.component';
import { LooseCustomField } from '../../contracts/loose-custom-field';
import { RecordService } from './../../../../../services/record.service';
import { NotificationService } from '../../../../../services/notification.service';
import { ViewService } from './../../../../../services/view.service';
import { CUSTOM_MODULE_TEMPLATES } from '../../constants';
import { CustomTemplate } from '../../contracts/custom-template';
import { filter, map, switchMap } from 'rxjs/operators';
import { SubscriptionRestrictionService } from './../../../../../services/subscription-restriction/subscription-restriction.service';
import { ADVANCED_PLAN } from './../../../../../objects/subscription-plans';
import { AnnualConditionReportComponent } from './dialog/annual-condition-report/annual-condition-report.component';
import { AnnualAssetReportComponent } from './dialog/annual-asset-report/annual-asset-report.component';
import { LooseObject } from './../../../../../objects/loose-object';
import { LooseModuleData } from '../../contracts/loose-module-data';
import { assign, isString, isEmpty } from 'lodash-es';
import { data_get, fallback, filled, data_is_true, data_is_false, blank } from '../../../../../shared/utils/common';
import { JobReportDialogComponent } from './dialog/job_report-dialog.component';
import FormTemplateHistory from '../../entities/form-template-history.entity';
import { FORM_TEMPLATE_DISPLAY_ORIENTATION, PAGE_FORMATS } from '../../constants';

@Component({
  selector: 'form-templates-generator',
  templateUrl: './generate-pdf.component.html',
  styleUrls: ['./generate-pdf.component.scss']
})
export class GeneratePDFComponent implements OnDestroy, OnInit {
  /**
   * element attribute that will determine the current module being used
   *
   * @type {string}
   */
  @Input('module-name') moduleName: string;

  /**
   * Current module identifier
   *
   * @type {string}
   */
  @Input('module-id') moduleId: string;

  /**
   * This contains the possible filters for document data fetching etc
   *
   * @type {LooseObject}
   */
  @Input('filter') filter: LooseObject;

  /**
   * this contains all the form templates  (active) for the current module
   *
   * @type {FormTemplate[]}
   */
  templates: FormTemplate[] = [];

  /**
   * determines if the current module templates are being fetched
   *
   * @type {boolean}
   */
  isLoadingTemplates: boolean = false;

  /**
   * list of component based on document type
   *
   * @type {object}
   */
  documentTypeComponent: object = {
    site_asset_summary: AnnualAssetReportComponent,
    site_annual_condition: AnnualConditionReportComponent,
    job_inspection_report: AnnualAssetReportComponent,
    job_report: JobReportDialogComponent,
  };

  /**
   * document type that will be using
   *
   * @type {string}
   */
  @Input('document-type') documentType: string | string[];

  /**
   * data from get_data request
   *
   * @type {LooseModuleData}
   */
  templateData: LooseModuleData;

  /**
   * Flagged to determined if the current feature should be displayed
   *
   * @type {boolean}
   */
  get shouldDisplay(): boolean {
    return this.isAdvancedOrEnterprise || this.customTemplates.length > 0;
  };

  /**
   * List of custom templates
   *
   * @type {CustomTemplate[]}
   */
  customTemplates: CustomTemplate[];

  /**
   * custom module data for pdf
   *
   * @type {LooseObject}
   */
  customModuleData: LooseObject = {};

  /**
   * Flag if the current plan is enterprise
   *
   * @type {boolean}
   */
  get isAdvancedOrEnterprise(): boolean {
    return this.plans.subscriptionPlanOfActiveClientIsGreaterThanOrEqualTo(ADVANCED_PLAN);
  };

  /**
   * Get the total length of default templates
   *
   * @type {number}
   */
  get totalDefaultTemplates(): number {
    return this.templates.filter(objTemplate =>
      data_is_false(data_get(objTemplate, 'is_custom_template'))
    ).length;
  };

  /**
   * Get the total length of custom templates
   *
   * @type {number}
   */
  get totalCustomTemplates(): number {
    return this.templates.filter(objTemplate => data_is_true(data_get(objTemplate, 'is_custom_template'))).length;
  };

  /**
   * INTERNAL: this contains the all subscriptions from observable that will be cleaned up after
   * this component is being destroyed
   */
  private subscriptions: Subscription[] = [];

  isInProgress$ = new BehaviorSubject<boolean>(false);

  /**
   * This indicates that all default templates should be excluded when listing the available templates
   *
   * @type {boolean}
   */
  @Input('exclude-defaults') excludeDefaultTemplates: boolean = false;

  /**
   * @type {boolean}
   */
  @Input('allow-custom-template-generate') allowCustomTemplateGenerate: boolean = false;
  @Input('previous-preview-data') previousPreviewData: PreviewPdfData;

  /**
   * @type {string}
   */
  @Input('preview-header-label') previewHeaderLabel: string;

  readonly notices$ = new BehaviorSubject<Record<string, string>>({
    'job_inspection_report': 'Please use the job report template',
    'job_work_order': 'Please use the job report template',
  });

  /**
   * @param {FormTemplateService} service
   * @param {MatDialog}           dialogFactory
   */
  constructor(
    protected service: FormTemplateService,
    protected dialogFactory: MatDialog,
    protected records: RecordService,
    protected views: ViewService,
    protected ctx: Injector,
    protected plans: SubscriptionRestrictionService,
    private _notif: NotificationService,
  ) { }

  /**
   * {@inheritdoc}
   */
  ngOnInit() {
    this.templates = [];
    this.customTemplates = (CUSTOM_MODULE_TEMPLATES[this.moduleName] || []).filter((template) => this.shouldDisplayCustomTemplate(template));
  }

  /**
   * retrieves all the module template
   *
   * @returns {boolean}
   */
  getTemplates(): boolean {
    // this action should only be applicable to entprise subscribers
    if (this.templates.length < 1) {
      this.loadTemplates();
    }

    return false;
  }

  /**
   * callback when a form template was clicked and will generate a PDF preview
   *
   * @param   {FormTemplate|undefined} template
   *
   * @returns {boolean}
   */
  previewTemplate(template?: FormTemplate): boolean {
    if (template) {
      if (this.templateHasNoOdtFile(template)) {
        this._notif.notifyError('template_has_no_odt_file');
        return false;
      }
    }

    // if already in progress
    if (this.isInProgress$.getValue()) {
      return false;
    }

    if (isEmpty(this.customModuleData)) {
      // Close all previous dialogs
      this.dialogFactory.closeAll();
    }

    // if we had a previous preview data given from the preview component
    // we would regenerate a new preview with the selected template
    if (this.previousPreviewData !== undefined) {
      let htmlBuilderData = null;

      if (data_is_true(data_get(template, 'isHTML'))) {
        htmlBuilderData = {
          html: data_get(template, 'html_data'),
          css: data_get(template, 'css_data')
        };
      }

      this.openPreviewComponent(template, this.previousPreviewData.moduleData, {
        custom: this.previousPreviewData.custom,
        filename: this.previousPreviewData.filename,
      }, htmlBuilderData);
    } else {
      this.isInProgress$.next(true);

      defer(() => {
        // This might not work when template was not provided since additional component
        // are based on the document type of a given template. Improve me...
        let documentTypeComponent = this.documentTypeComponent[data_get(template, ['documentType'])] || null;

        if (!!documentTypeComponent) {
          const record = this.views.getViewRecord();

          return this.dialogFactory.open(documentTypeComponent, {
            minWidth: '30vw',
            maxWidth: '100%',
            height: 'auto',
            data: {
              id: this.moduleId,
              module_name: template.module,
              has_custom_field: data_get(template, ['history', 'metadata', 'fields'], {
                default_value: [],
              }).length > 0,
              has_job_checklist: data_get(record, 'has_job_checklist', { default_value: false })
            },
          })
            .afterClosed()
            .pipe(
              filter((filter) => !!filter)
            );
        }

        return of(undefined);
      })
        .pipe(
          switchMap((filter) => {
            if (this.moduleId) {
              return this.getModuleMetadata$({
                dontIncludeTemplate: !!template,
                documentType: data_get(template, 'documentType'),
                filter: assign({}, filter, this.filter),
                data: this.customModuleData,
                isHTML: data_get(template, 'isHTML', {
                  default_value: false,
                }),
                is_default_report: data_is_false(
                  data_get(template, 'is_custom_template')
                ) || blank(template),
              });
            } else {
              return this.getModuleTemplate$({
                dontIncludeTemplate: !!template,
                documentType: data_get(template, 'documentType'),
                filter: assign({}, filter, this.filter),
                data: this.customModuleData,
                isHTML: data_get(template, 'isHTML', {
                  default_value: false,
                })
              });
            }
          }),
          finalize(() => this.isInProgress$.next(false)),
          map((result) => ({
            ...result,
            template: template || result.template,
          })),
          map((result) => ({
            ...result,
            template: {
              ...result.template,
              history: {
                ...result.template.history,
                // merges the additional paths to the current template paths
                path: [
                  ... (isString(data_get(result, 'template.history.path')) ? [data_get(result, 'template.history.path', { default_value: '' })] : data_get(result, 'template.history.path', { default_value: '' })) as string[],
                  ...result.additional_template_paths
                ]
              },
              /// if 'display_orientation' is returned from the api call, use that
              ... (filled(data_get(result, 'display_orientation')) && {
                display_orientation: fallback(data_get(result, 'display_orientation'), {
                  fallback: () => 'landscape',
                }) as FORM_TEMPLATE_DISPLAY_ORIENTATION,
              }),
              /// if 'page_format' is returned from the api call, use that
              ... (filled(data_get(result, 'page_format')) && {
                page_format: fallback(data_get(result, 'page_format'), {
                  fallback: () => 'a4',
                }) as PAGE_FORMATS,
              }),
            }
          })),
          switchMap((result) => {
            const numFieldsLength = data_get(result, 'template.history.metadata.fields.length', { default_value: 0 });

            if (numFieldsLength > 0) {
              return this.dialogFactory.open(CustomFieldsComponent, {
                minWidth: '30vw',
                maxWidth: '100%',
                height: 'auto',
                data: {
                  moduleData: result.data,
                  fields: template.history.metadata.fields,
                  id: this.moduleId,
                  module: this.moduleName,
                },
              })
                .afterClosed()
                .pipe(
                  filter((value) => !!value),
                  map((custom) => ({
                    ...result,
                    custom,
                  }))
                );
            }

            return of(result);
          })
        )
        .subscribe(async (result) => {
          let option = {
            filename: result.config.filename,
            custom: data_get(result, 'custom')
          };
          if (result['additional_document']) {
            option.custom = {
              ... fallback(option.custom, {
                fallback: () => ({}),
              }),
              additional_document: result['additional_document'],
              document_sorting: fallback(data_get(result, 'document_sorting'), {
                fallback: () => [],
              }),
            };
          }

          let htmlBuilderData = data_get(result, 'html_builder_data');

          if (filled(htmlBuilderData)) {
            if (blank(data_get(htmlBuilderData, 'html'))) {
              htmlBuilderData = {
                html: data_get(template, 'html_data'),
                css: data_get(template, 'css_data'),
              };
            }

            if (blank(data_get(htmlBuilderData, 'html')) && data_is_true(data_get(template, 'is_custom_template'))) {
              htmlBuilderData['html'] = await this.service.getDefaultHtmlEditorBody(
                template.module,
                data_get(template, 'documentType', { default_value: '' })
              );
            }

            if (blank(data_get(htmlBuilderData, 'css')) && data_is_true(data_get(template, 'is_custom_template'))) {
              htmlBuilderData['css'] = await this.service.getDefaultCssEditorBody();
            }

            if (blank(data_get(htmlBuilderData, 'html'))) {
              htmlBuilderData = null;
            }
          } else {
            if (data_is_true(data_get(template, 'isHTML'))) {
              if (filled(data_get(template, 'html_data'))) {
                htmlBuilderData = {
                  html: data_get(template, 'html_data'),
                  css: data_get(template, 'css_data'),
                };
              } else {
                htmlBuilderData = {
                  html: await this.service.getDefaultHtmlEditorBody(
                    template.module,
                    data_get(template, 'documentType', { default_value: '' })
                  ),
                  css: await this.service.getDefaultCssEditorBody(),
                }
              }
            } else {
              htmlBuilderData = null;
            }
          }

          this.openPreviewComponent(result.template, result.data, option, htmlBuilderData)
        });
    }

    return false;
  }

  setCustomModuleData(moduleData: LooseObject): void {
    this.customModuleData = moduleData;
  }

  templateHasNoOdtFile(template: FormTemplate): boolean {
    return !this._hasHistory(template.history) && !template.isHTML && data_is_true(data_get(template, 'is_custom_template'));
  }

  /**
   * {@inheritdoc}
   */
  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * opens a preview component for previewing the template
   *
   * @param   {FormTemplate}               template
   * @param   {LooseModuleData}            moduleData
   * @param   {OpenPreviewComponentOption} option
   *
   * @returns {void}
   */
  protected openPreviewComponent(
    template: FormTemplate,
    moduleData: LooseModuleData,
    option: OpenPreviewComponentOption,
    html_builder_data: { html: string, css: string } | null = null
  ): void {
    const bUseHtml: boolean = data_is_true(data_get(template, 'isHTML')) && filled(html_builder_data);

    this.dialogFactory.open(PreviewComponent, {
      minWidth: '50%',
      maxWidth: '100%',
      height: 'auto',
      data: {
        moduleData: moduleData,
        filename: option.filename,
        module: this.moduleName,
        moduleId: this.moduleId,
        template: template,
        ...(option.custom && { custom: option.custom }),
        allowCustomTemplateGeneration: this.allowCustomTemplateGenerate,
        previewHeaderLabel: this.previewHeaderLabel,
        ...(bUseHtml && {
          html_builder_data: {
            html: html_builder_data.html,
            css: html_builder_data.css,
          }
        }),
        display_orientation: template.display_orientation,
        page_format: template.page_format
      }
    });
  }

  /**
   * Do some additional checks to determine if the custom template should be displayed
   *
   * @param {CustomTemplate} customTemplate
   *
   * @returns {boolean}
   */
  protected shouldDisplayCustomTemplate(customTemplate: CustomTemplate): boolean {
    if (customTemplate.minimum_subscription_plan !== undefined) {
      return this.plans.subscriptionPlanOfActiveClientIsGreaterThanOrEqualTo(customTemplate.minimum_subscription_plan);
    }

    return true;
  }

  /**
   * Load all available templates
   *
   * @returns {void}
   */
  protected loadTemplates(): void {
    this.isLoadingTemplates = true;

    let subscription = this.service.getModuleTemplates$(this.moduleName, this.excludeDefaultTemplates)
      .pipe(
        finalize(() => this.isLoadingTemplates = false),
        take(1)
      )
      .subscribe((templates: FormTemplate[]) => {
        this.templates = templates.filter((template) => {
          // only filter when provided
          if (template.minSubscription) {
            return this.plans.subscriptionPlanOfActiveClientIsGreaterThanOrEqualTo(template.minSubscription)
          }

          return true;
        })

        const promises = this.templates.map((template) => {
          /// For custom HTML templates with blank data, fill with defaults.
          if (data_is_true(data_get(template, 'isHTML')) && blank(data_get(template, 'html_data')) && data_is_true(data_get(template, 'is_custom_template'))) {
            return this.service.getDefaultHtmlEditorBody(
              data_get(template, 'module'),
              data_get(template, 'documentType', { default_value: '' })
            ).then((html_data) => {
              template.html_data = html_data;

              return this.service.getDefaultCssEditorBody();
            }).then((css_data) => {
              template.css_data = css_data;

              return template;
            });
          }

          return Promise.resolve(template);
        });

        Promise.all(promises)
          .then((resolvedTemplates: FormTemplate[]) => {
            this.templates = resolvedTemplates;
          });
      });

    this.subscriptions.push(subscription);
  }

  /**
   * Wraps fetching of module metadata information
   *
   * @param {GetModuleMetadataOptions} options
   */
  protected getModuleMetadata$(options: GetModuleMetadataOptions = {}) {
    return this.service.getModuleDocumentData$(this.moduleId, this.moduleName, {
      dontIncludeTemplate: options.dontIncludeTemplate || false,
      filter: options.filter,
      document_type: options.documentType || this.documentType,
      data: options.data,
      isHTML: options.isHTML,
      is_default_report: options.is_default_report
    });
  }

  protected getModuleTemplate$(options: GetModuleMetadataOptions = {}) {
    return this.service.getModuleDocumentTemplate$(this.moduleName, {
      dontIncludeTemplate: options.dontIncludeTemplate || false,
      filter: options.filter,
      document_type: options.documentType || this.documentType,
      data: options.data,
      isHTML: options.isHTML,
    });
  }

  private _hasHistory(history: FormTemplateHistory): boolean {
    return filled(data_get(history, 'path'));
  }
}

interface GetModuleMetadataOptions {
  dontIncludeTemplate?: boolean;
  filter?: LooseObject;
  documentType?: string | string[];
  data?: Record<string, any>;
  isHTML?: boolean;
  /// FC-4537: checks if the report is a system default, like 'Job Report'
  is_default_report?: boolean;
}

interface OpenPreviewComponentOption {
  filename: string;
  custom?: LooseCustomField;
}